import logger from './logger.js';
// Set at build time (rollup.config.js); a missing or invalid key leaves the page untouched.
const API_KEY = process.env.AMPLITUDE_API_KEY ?? '';
const STYLE_ID = 'amp-exp-css';
// Usercentrics service id for Amplitude.
const CONSENT_SERVICE_ID = 'Sk9kb5VoOi-7';
// Longest time the page stays hidden.
const TIMEOUT_MS = 1000;
const AMPLITUDE_SCRIPT = /^https:\/\/cdn(\.eu)?\.amplitude\.com\/script\/[0-9a-f]{32}(\.experiment)?\.js(\?.*)?$/;
// Amplitude's own anti-flicker style; the runtime removes #amp-exp-css once variants are applied.
const MASK = '*{visibility:hidden!important;background-image:none!important}';
let status = 'idle';
let desired = null;
let pushed = null;
let lastError = null;
/** For support checks in the browser console: disw.experiment.getState(). */
export function getState() {
    return { status, consent: desired, error: lastError };
}
function fail(message, detail) {
    lastError = detail instanceof Error ? `${message}: ${detail.message}` : message;
    logger.error(`[disw.experiment] ${lastError}`);
}
/** Maps a Usercentrics service record; only an explicit refusal is 'denied'. */
export function decisionOf(record) {
    if (!record || typeof record !== 'object') {
        return 'pending';
    }
    if (record.status === true) {
        return 'granted';
    }
    const history = Array.isArray(record.history) ? record.history : [];
    const last = history[history.length - 1];
    if (record.status === false && last?.type === 'explicit' && last.status === false) {
        return 'denied';
    }
    return 'pending';
}
function storedDecision() {
    try {
        const services = JSON.parse(window.localStorage.getItem('uc_settings') ?? 'null')?.services;
        if (Array.isArray(services)) {
            return decisionOf(services.find(service => service?.id === CONSENT_SERVICE_ID));
        }
    }
    catch {
        // unreadable consent state means "no decision"
    }
    return 'pending';
}
function liveDecision() {
    try {
        const services = window.UC_UI?.getServicesBaseInfo?.();
        const service = Array.isArray(services) ? services.find(entry => entry?.id === CONSENT_SERVICE_ID) : undefined;
        if (service) {
            return decisionOf(service.consent);
        }
    }
    catch {
        // fall back to the persisted record
    }
    return storedDecision();
}
function amplitudePresent() {
    if (window.webExperiment || window.WebExperiment || document.getElementById(STYLE_ID)) {
        return true;
    }
    return Array.from(document.scripts).some(script => AMPLITUDE_SCRIPT.test(script.src));
}
/** True when disw.init runs after the page may already be on screen (for example on DOMContentLoaded). */
function pageMayBeVisible() {
    if (document.readyState !== 'loading') {
        return true;
    }
    try {
        return performance.getEntriesByType('paint').length > 0;
    }
    catch {
        return false;
    }
}
/** Keeps any later Amplitude experiment script from changing this page view. */
function excludePageView() {
    if (window.webExperiment && !window.webExperiment.isStub) {
        return false;
    }
    window.webExperiment = {
        isStub: false,
        isRunning: false,
        excluded: true,
        start: () => Promise.resolve(),
        setConsentStatus: () => undefined,
    };
    return true;
}
/**
 * Starts Amplitude Web Experiment for this page view. disw.init calls it on every site that
 * configures MTM, unless the site sets `experiment: false`.
 */
export function initializeExperiment(enabled) {
    if (status !== 'idle') {
        return getState();
    }
    try {
        if (!enabled) {
            status = 'disabled';
        }
        else if (!/^[0-9a-f]{32}$/.test(API_KEY)) {
            fail('no valid AMPLITUDE_API_KEY in this build; Amplitude Web Experiment not loaded');
            status = 'invalid-config';
        }
        else if (pageMayBeVisible()) {
            // Hiding a page that is already visible would blank it, so the site keeps today's behaviour.
            status = 'late-init';
        }
        else if (amplitudePresent()) {
            status = 'runtime-already-present';
        }
        else {
            run();
        }
    }
    catch (error) {
        fail('unexpected error; page shown unchanged', error);
        excludePageView();
        status = 'error';
    }
    return getState();
}
function run() {
    desired = storedDecision();
    let settled = false;
    let timer;
    let observer;
    const style = document.createElement('style');
    const nonce = document.currentScript?.nonce ?? '';
    // A refusal is sent only after the variant is applied, so the runtime keeps serving it from memory.
    function pushConsent() {
        if (desired === null || desired === 'pending' || desired === pushed) {
            return;
        }
        const runtime = window.webExperiment;
        if (typeof runtime?.setConsentStatus !== 'function') {
            return;
        }
        if (desired === 'denied' && !settled) {
            return;
        }
        pushed = desired;
        try {
            runtime.setConsentStatus(desired);
        }
        catch (error) {
            fail('setConsentStatus failed', error);
        }
    }
    function onDecision() {
        const next = liveDecision();
        if (next === 'pending' || next === desired) {
            return;
        }
        desired = next;
        const consentOptions = window.experimentConfig?.consentOptions;
        // A runtime that has not started yet reads the config; 'denied' is only ever sent at runtime.
        if (next === 'granted' && consentOptions) {
            consentOptions.consentStatus = 'granted';
        }
        pushConsent();
    }
    function finish(result) {
        if (settled) {
            return;
        }
        settled = true;
        if (timer !== undefined) {
            clearTimeout(timer);
        }
        observer?.disconnect();
        style.remove();
        status = result;
        if (result !== 'released') {
            const excluded = excludePageView();
            // A runtime that started but has not applied yet may still change the page.
            if (result === 'timeout-excluded' && !excluded) {
                status = 'timeout-revealed';
            }
        }
        pushConsent();
    }
    window.experimentConfig = {
        ...(window.experimentConfig ?? {}),
        consentOptions: { consentRequired: true, consentStatus: desired === 'granted' ? 'granted' : 'pending' },
    };
    try {
        window.addEventListener('UC_SDK_EVENT', event => {
            if (event.detail?.event === 'consent_status') {
                onDecision();
            }
        });
        window.addEventListener('UC_UI_CMP_EVENT', onDecision);
        style.id = STYLE_ID;
        if (nonce) {
            style.nonce = nonce;
        }
        style.textContent = MASK;
        document.head.appendChild(style);
        observer = new MutationObserver(() => {
            if (!style.isConnected) {
                finish('released');
            }
        });
        observer.observe(document.head, { childList: true });
        timer = setTimeout(() => finish('timeout-excluded'), TIMEOUT_MS);
        const script = document.createElement('script');
        script.src = `https://cdn.eu.amplitude.com/script/${API_KEY}.experiment.js`;
        script.async = true;
        // Script-inserted async scripts default to Low priority and queue behind page images.
        script.setAttribute('fetchpriority', 'high');
        if (nonce) {
            script.nonce = nonce;
        }
        script.onload = pushConsent;
        script.onerror = () => {
            fail('Amplitude Web Experiment script failed to load');
            finish('script-error');
        };
        document.head.appendChild(script);
        status = 'held';
    }
    catch (error) {
        fail('unexpected error; page shown unchanged', error);
        finish('error');
    }
}
export default { getState };
